import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import BackofficeLayout from '@/layouts/BackofficeLayout';
import Login from '@/pages/auth/Login';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import Dashboard from '@/pages/dashboard/Dashboard';
import Revenue from '@/pages/revenue/Revenue';
import ChangelogList from '@/pages/changelog/ChangelogList';
import ChangelogDetail from '@/pages/changelog/ChangelogDetail';
import TemplatesList from '@/pages/templates/TemplatesList';
import TemplateDetail from '@/pages/templates/TemplateDetail';
import AffiliatesList from '@/pages/affiliates/AffiliatesList';
import AffiliateDetail from '@/pages/affiliates/AffiliateDetail';
import AgenciesList from '@/pages/agencies/AgenciesList';
import AgencyDetail from '@/pages/agencies/AgencyDetail';
import CampaignsList from '@/pages/campaigns/CampaignsList';
import Settings from '@/pages/settings/Settings';
import UsersList from '@/pages/users/UsersList';
import UserDetail from '@/pages/users/UserDetail';
import TeamPage from '@/pages/team/TeamPage';
import XraySettings from '@/pages/xray/XraySettings';
import XrayCompliance from '@/pages/xray/XrayCompliance';
import OnboardingAppointments from '@/pages/onboarding/OnboardingAppointments';
import XrayOverview from '@/pages/xray/XrayOverview';
import TrackingPage from '@/pages/tracking/TrackingPage';
import LookupPage from '@/pages/lookup/LookupPage';
import AiPreviewPage from '@/pages/labs/ai-preview/AiPreviewPage';
import EmailsPage from '@/pages/emails/EmailsPage';
import { EnvironmentIndicator } from '@/components/EnvironmentIndicator';

// Protected Route Wrapper
const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">Loading...</div>;
  }

  if (!user || (!user.isAdmin)) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Check Auth Wrapper
const AuthRoute = ({ children }: { children: JSX.Element }) => {
  const { user, loading } = useAuth();
  
  if (loading) return null;
  if (user && user.isAdmin) return <Navigate to="/" replace />;
  return children;
};

export default function App() {
  return (
    <>
      <EnvironmentIndicator />
      {/* Toast notifications — styled to match Dashboard */}
      <Toaster
        position="bottom-right"
        containerStyle={ { zIndex: 99999 } }
        toastOptions={ {
          duration: 4000,
          style: {
            background: '#ffffff',
            color: '#1f2937',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '14px',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: '#ffffff',
            },
            style: {
              background: '#ffffff',
              border: '1px solid rgba(34, 197, 94, 0.3)',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#ffffff',
            },
            style: {
              background: '#ffffff',
              border: '1px solid rgba(239, 68, 68, 0.3)',
            },
            duration: 5000,
          },
          loading: {
            iconTheme: {
              primary: '#6366f1',
              secondary: '#ffffff',
            },
          },
        } }
      />

      <Routes>
        <Route path="/login" element={<AuthRoute><Login /></AuthRoute>} />
        <Route path="/forgot-password" element={<AuthRoute><ForgotPassword /></AuthRoute>} />
      
      <Route path="/" element={<ProtectedRoute><BackofficeLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="revenue" element={<Revenue />} />

        {/* Lookup */}
        <Route path="lookup" element={<LookupPage />} />

        {/* Labs */}
        <Route path="labs/ai-preview" element={<AiPreviewPage />} />

        {/* Changelog Routes */}
        <Route path="changelog" element={<ChangelogList />} />
        <Route path="changelog/:slug" element={<ChangelogDetail />} />

        <Route path="templates" element={<TemplatesList />} />
        <Route path="templates/:id" element={<TemplateDetail />} />

        <Route path="emails" element={<EmailsPage />} />

        <Route path="affiliates" element={<AffiliatesList />} />
        <Route path="affiliates/:id" element={<AffiliateDetail />} />
        <Route path="agencies" element={<AgenciesList />} />
        <Route path="agencies/:id" element={<AgencyDetail />} />

        <Route path="campaigns" element={<CampaignsList />} />

        <Route path="users" element={<UsersList />} />
        <Route path="users/:id" element={<UserDetail />} />

        <Route path="team" element={<TeamPage />} />

        {/* Onboarding Routes */}
        <Route path="onboarding" element={<Navigate to="/onboarding/appointments" replace />} />
        <Route path="onboarding/appointments" element={<OnboardingAppointments />} />

        {/* X-Ray Routes */}
        <Route path="xray" element={<Navigate to="/xray/overview" replace />} />
        <Route path="xray/overview" element={<XrayOverview />} />
        <Route path="xray/settings" element={<XraySettings />} />
        <Route path="xray/compliance" element={<XrayCompliance />} />

        {/* Tracking (Meta CAPI for our own funnel) */}
        <Route path="tracking" element={<TrackingPage />} />

        {/* Settings Routes */}
        <Route path="settings" element={<Settings />} />
        <Route path="settings/:tab" element={<Settings />} />
      </Route>
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

